import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  SchoolMessageSettingsRequest,
  SchoolMessageSettingsState,
  initialSchoolMessageSettingsState,
} from '../entities/school-message-settings.model';
import { SchoolMessageSettingsService } from '../infra/school-message-settings.service';

const OPS = ['load', 'update'] as const;

/**
 * Per-tenant school access-denial messages (SchoolService `/MessageSettings`).
 * A single record: `load` reads the tenant's messages and `update` patches
 * them (only the provided fields change). This is stored config only — the
 * access edge renders the messages; the store never gates entry itself.
 */
export const SchoolMessageSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<SchoolMessageSettingsState>(initialSchoolMessageSettingsState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(SchoolMessageSettingsService),
      errorStore = inject(ErrorStore),
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (messageSettings) =>
                  patchState(store, { messageSettings }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) => patchState(store, setPropError('load', e)),
              }),
            ),
          ),
        ),
      ),

      update: rxMethod<SchoolMessageSettingsRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (messageSettings) =>
                  patchState(store, { messageSettings }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              }),
            ),
          ),
        ),
      ),

      reset(): void {
        patchState(store, initialSchoolMessageSettingsState, setPropInit(...OPS));
      },
    }),
  ),
);
